import {
  Box,
  BoxProps,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { BarSeriesOption, TooltipComponentOption } from "echarts";
import { groupBy, sumBy } from "lodash";
import { useMemo, useState } from "react";

import { components } from "@/@types/api";
import HorizontalBarChart from "@/Components/v2/molecule/BarChart/HorizontalBarChart";
import { graphColors } from "@/Components/v2/utils/brand-colors";
import { ABBREVIATED_OPTION, useConfig } from "@/hooks/useConfig";
import { formatPercentageFromFraction } from "@/pages/scenario-builder/utils/formatPercentage";
import { isDefined } from "@/Utils/array-utils";

import { truncateText } from "../../../utils";
import { calculateROI } from "../../../utils/spend-revenue-summary";

type Revenue = components["schemas"]["ScenarioResultRevenue"];
type Spend = components["schemas"]["ScenarioResultSpend"];

interface ROIDBreakdownProps extends BoxProps {
  revenues: Revenue[];
  spends: Spend[];
}

const tooltip = (
  formatter: (amount: number) => string,
  originalChannels: string[]
): TooltipComponentOption => ({
  trigger: "axis",
  // params is always an array in this case, even for ROI
  formatter: (params: any) => {
    // Handle multiple series (spend vs revenue)
    const firstParam = params.find(
      (p: any) => p.seriesName === "Marketing Spend" || p.seriesIndex === 0
    );
    const secondParam = params.find(
      (p: any) => p.seriesName === "Marketing Revenue" || p.seriesIndex === 1
    );

    // Get the original channel name using the data index
    const channelName =
      originalChannels[params[0]?.dataIndex] || params[0]?.name || params[0]?.axisValue;

    const spendValue =
      firstParam && isDefined(firstParam.value) ? formatter(firstParam.value) : "N/A";
    const revenueValue =
      secondParam && isDefined(secondParam.value) ? formatter(secondParam.value) : "N/A";

    return `
          <div style='padding: 16px; border-radius: 4px;'>
            <div style='display: flex; flex-direction: column; gap: 0.5rem;'>
              <div style='font-weight: 600;'>${channelName}</div>
              ${firstParam ? `<div style='display: flex; align-items: center; gap: 0.5rem;'><div style='width: 12px; height: 12px; background-color: ${firstParam.color}; border-radius: 2px;'></div><div>${firstParam.seriesName}: ${spendValue}</div></div>` : ""}
              ${secondParam ? `<div style='display: flex; align-items: center; gap: 0.5rem;'><div style='width: 12px; height: 12px; background-color: ${secondParam.color}; border-radius: 2px;'></div><div>${secondParam.seriesName}:  ${revenueValue}</div></div>` : ""}
            </div>
          </div>
        `;
  },
});

const BreakdownOptions = [
  {
    type: "roi",
    label: "ROI (%)",
  },
  {
    type: "spend_vs_revenue_dollars",
    label: "Spend vs Revenue ($)",
  },
  {
    type: "spend_vs_revenue_percentage",
    label: "Spend vs Revenue (%)",
  },
] as const;

type BreakdownType = (typeof BreakdownOptions)[number]["type"];

const getChannelSpendRevenueSummary = (revenues: Revenue[], spends: Spend[]) => {
  const totalSpend = sumBy(spends, "spend");
  const totalRevenue = sumBy(revenues, "revenue");

  const spendsByChannel = Object.entries(groupBy(spends, "channel")).map(([channel, spends]) => ({
    channel,
    totalSpend: sumBy(spends, "spend"),
  }));

  const revenuesByChannel = Object.entries(groupBy(revenues, "channel")).map(
    ([channel, revenues]) => ({
      channel,
      totalRevenue: sumBy(revenues, "revenue"),
    })
  );

  const channelSummary = spendsByChannel
    .map((spend) => {
      const revenue = revenuesByChannel.find((revenue) => revenue.channel === spend.channel);
      if (!revenue) {
        return null;
      }

      return {
        channel: spend.channel,
        totalSpend: spend.totalSpend,
        totalRevenue: revenue.totalRevenue,
        roi: calculateROI({ spend: spend.totalSpend, revenue: revenue.totalRevenue }),
      };
    })
    .filter(isDefined)
    .sort((a, b) => a.channel.localeCompare(b.channel));

  return { channelSummary, totalSpend, totalRevenue };
};

const transformChannelSummaryForBreakdownType = (
  spendRevenueSummary: ReturnType<typeof getChannelSpendRevenueSummary>,
  breakdownType: BreakdownType
) => {
  const sortingFnByChannelReverseForChart = (a: { name: string }, b: { name: string }) =>
    b.name.localeCompare(a.name);

  if (breakdownType === "roi") {
    return {
      type: "roi" as const,
      data: spendRevenueSummary.channelSummary
        .map((spend) => ({
          name: spend.channel,
          totalROI: spend.roi,
        }))
        .sort(sortingFnByChannelReverseForChart),
    };
  }

  if (breakdownType === "spend_vs_revenue_dollars") {
    return {
      type: "spend_vs_revenue_dollars" as const,
      data: spendRevenueSummary.channelSummary
        .map((spend) => ({
          name: spend.channel,
          totalSpend: spend.totalSpend,
          totalRevenue: spend.totalRevenue,
        }))
        .sort(sortingFnByChannelReverseForChart),
    };
  }

  return {
    type: "spend_vs_revenue_percentage" as const,
    data: spendRevenueSummary.channelSummary
      .map((spend) => ({
        name: spend.channel,
        percentageSpend:
          spendRevenueSummary.totalSpend > 0
            ? spend.totalSpend / spendRevenueSummary.totalSpend
            : 0,
        percentageRevenue:
          spendRevenueSummary.totalRevenue > 0
            ? spend.totalRevenue / spendRevenueSummary.totalRevenue
            : 0,
      }))
      .sort(sortingFnByChannelReverseForChart),
  };
};

export const ROIBreakdown = ({ revenues, spends, ...boxProps }: ROIDBreakdownProps) => {
  const theme = useTheme();
  const [selectedBreakdownType, setSelectedBreakdownType] = useState<BreakdownType>(
    "spend_vs_revenue_dollars"
  );

  const { config } = useConfig();
  const { formatCurrency } = config.currency;

  const channelSummary = useMemo(() => {
    return getChannelSpendRevenueSummary(revenues, spends);
  }, [revenues, spends]);

  const transformedChannelSummary = useMemo(() => {
    return transformChannelSummaryForBreakdownType(channelSummary, selectedBreakdownType);
  }, [channelSummary, selectedBreakdownType]);

  const chartSeries = useMemo<BarSeriesOption[]>(() => {
    if (transformedChannelSummary.type === "roi") {
      return [
        {
          name: "ROI",
          color: theme.palette.roiGraph.layer.brand,
          data: transformedChannelSummary.data.map((spend) => ({
            name: spend.name,
            value: spend.totalROI ?? 0,
            itemStyle: {
              // For negative ROI, curve left side; for positive ROI, curve right side
              borderRadius: (spend.totalROI ?? 0) < 0 ? [5, 0, 0, 5] : [0, 5, 5, 0],
            },
          })),
        },
      ];
    }

    if (transformedChannelSummary.type === "spend_vs_revenue_dollars") {
      return [
        {
          name: "Marketing Spend",
          color: graphColors.graphBlue100,
          itemStyle: {
            borderRadius: [0, 5, 5, 0], // Curved right end for horizontal bars
          },
          data: transformedChannelSummary.data.map((spend) => ({
            name: spend.name,
            value: spend.totalSpend,
          })),
        },

        {
          name: "Marketing Revenue",
          color: graphColors.graphPurple100,
          itemStyle: {
            borderRadius: [0, 5, 5, 0], // Curved right end for horizontal bars
          },
          data: transformedChannelSummary.data.map((spend) => ({
            name: spend.name,
            value: spend.totalRevenue,
          })),
        },
      ];
    }
    return [
      {
        name: "Marketing Spend",
        color: graphColors.graphBlue100,
        itemStyle: {
          borderRadius: [0, 5, 5, 0], // Curved right end for horizontal bars
        },
        data: transformedChannelSummary.data.map((spend) => ({
          name: spend.name,
          value: spend.percentageSpend,
        })),
      },
      {
        name: "Marketing Revenue",
        color: graphColors.graphPurple100,
        itemStyle: {
          borderRadius: [0, 5, 5, 0], // Curved right end for horizontal bars
        },
        data: transformedChannelSummary.data.map((spend) => ({
          name: spend.name,
          value: spend.percentageRevenue,
        })),
      },
    ];
  }, [transformedChannelSummary]);

  const channels = channelSummary.channelSummary.map((spend) => spend.channel);
  const originalChannels = [...channels]; // Store original channel names before reversal

  // Chart overrides with scrolling for many channels
  const chartOverrides = useMemo(() => {
    const ROWS_TO_SHOW = 15;
    const startScrollValue = 100;
    const endScrollValue =
      channels.length > ROWS_TO_SHOW
        ? startScrollValue - (ROWS_TO_SHOW / channels.length) * 100
        : 0;

    return {
      ...(channels.length > ROWS_TO_SHOW && {
        dataZoom: [
          {
            type: "slider",
            yAxisIndex: 0,
            start: startScrollValue,
            end: endScrollValue,
            handleSize: 0,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            fillerColor: "rgba(0, 0, 0, 0.25)",
            borderColor: "rgba(0, 0, 0, 0)",
            showDetail: false,
            showDataShadow: false,
            zoomLock: true,
            width: 0,
            handleIcon: "M50,0 L50,100 100,100 100,0 Z",
            throttle: 33,
            realtime: true,
          },
          {
            type: "inside",
            yAxisIndex: 0,
            start: startScrollValue,
            end: endScrollValue,
            zoomOnMouseWheel: false,
            moveOnMouseMove: true,
            moveOnMouseWheel: true,
            throttle: 33,
            realtime: true,
          },
        ],
      }),
    };
  }, [channels.length]);

  const xAxisLabelFormatter = useMemo(() => {
    if (
      selectedBreakdownType === "roi" ||
      selectedBreakdownType === "spend_vs_revenue_percentage"
    ) {
      return formatPercentageFromFraction;
    }
    return (value: number) => formatCurrency(value, ABBREVIATED_OPTION);
  }, [selectedBreakdownType, formatCurrency]);

  const tooltipOptions = useMemo(() => {
    if (
      selectedBreakdownType === "roi" ||
      selectedBreakdownType === "spend_vs_revenue_percentage"
    ) {
      return tooltip(formatPercentageFromFraction, originalChannels.reverse());
    }

    return tooltip(
      (value) => formatCurrency(value, { abbreviate: true }),
      originalChannels.reverse()
    );
  }, [formatCurrency, selectedBreakdownType, originalChannels]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      border={`1px solid ${theme.palette.section.border}`}
      p={3}
      borderRadius={3}
      {...boxProps}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="start">
        <Stack direction="column">
          <Typography variant="h5" fontWeight={600} mb={0.5}>
            ROI Breakdown
          </Typography>
          <Typography variant="body2" mb={2.5} maxWidth="600px">
            Based on your inputs and historic spend, this is the return on investment our model
            estimates you will see with this scenario.
          </Typography>
        </Stack>
        <Stack direction="column">
          <Typography variant="body1" fontWeight={600} mb={0.5}>
            Show:
          </Typography>
          <Select
            value={selectedBreakdownType}
            onChange={(e) => setSelectedBreakdownType(e.target.value as BreakdownType)}
            size="small"
            sx={{
              width: "250px",
            }}
          >
            {BreakdownOptions.map((option) => (
              <MenuItem key={option.type} value={option.type}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      <Divider />

      <Box
        sx={{
          height: "auto", // Let HorizontalBarChart manage its own height
          maxHeight: "48rem", // Set maximum height to prevent excessive growth
          overflow: "visible",
        }}
      >
        <HorizontalBarChart
          axisLabels={channels.reverse().map((channel) => truncateText(channel))} // Reverse channels as BarChart y-axis ascends vertically upwards
          series={chartSeries}
          xAxisLabelFormatter={xAxisLabelFormatter}
          tooltipOptions={tooltipOptions}
          overrides={chartOverrides}
        />
      </Box>
    </Box>
  );
};
